import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TenantSettingsService} from "../../../services/tenant_settings.service";
import {TenantSettings} from "../../../contracts/tenant_settings.interface";
import {DynamicDialogConfig, DynamicDialogRef} from "primeng/dynamicdialog";
import {Tenant} from "../../../contracts/tenant.interface";
import {ToggleSwitch} from "primeng/toggleswitch";
import {InputNumber} from "primeng/inputnumber";
import {DialogAction} from "../../../../shared/contracts/dialog.interface";
import {Button} from "primeng/button";
import {Divider} from 'primeng/divider';
import {Textarea} from 'primeng/textarea';
import {InputText} from 'primeng/inputtext';

@Component({
  selector: 'app-settings-dialog',
  imports: [
    ReactiveFormsModule,
    ToggleSwitch,
    FormsModule,
    InputNumber,
    Button,
    Divider,
    Textarea,
    InputText
  ],
  templateUrl: './settings-dialog.component.html',
  styleUrl: './settings-dialog.component.scss'
})
export class SettingsDialogComponent implements OnInit {
  tenant?: Tenant;
  initialSettings: TenantSettings = {};

  settingsForm: FormGroup;

  constructor(private fb: FormBuilder, private tenantSettingsService: TenantSettingsService, private ref: DynamicDialogRef, private config: DynamicDialogConfig) {
    this.settingsForm = this.fb.group({
      features: this.fb.group({
        templateName: [''],
        users: this.fb.group({
          limit: [null],
          enabled: [false]
        }),
        alertTimer: this.fb.group({
          minutes: [10],
          enabled: [false]
        }),
        automaticMessages: this.fb.group({
          enabled: [false],
          newConversation: this.fb.group({ enabled: [true],  text: ['Your message has been received and will be handled soon.'] }),
          assigned:        this.fb.group({ enabled: [true],  text: ['Your conversation has been assigned to {operator_name}.'] }),
          active:          this.fb.group({ enabled: [true],  text: ['{operator_name} is active in your conversation.'] }),
          closed:          this.fb.group({ enabled: [true],  text: ['Your conversation has been marked as resolved.'] }),
        }),
        revokeConversation: this.fb.group({
          enabled: [false]
        }),
        audioTranscription: this.fb.group({
          enabled: [false]
        }),
        plateDetection: this.fb.group({
          enabled: [false]
        })
      })
    });
  }

  static readonly DIALOG_CONFIG: DynamicDialogConfig = {
    header: 'Dati Azienda',
    width: '95vw',
    height: 'max-content',
    style: {'max-width': '800px'},
    modal: true,
    focusOnShow: false,
  };

  ngOnInit() {
    if(this.config.data){
      this.tenant = this.config.data.tenant;
    }

    this.tenantSettingsService.get(this.tenant!.id!).subscribe(data => {
      this.initialSettings = data;
      this.settingsForm.patchValue(this.initialSettings);

      this.setupDependentControls();
    });
  }

  private setupDependentControls() {
    const usersGroup = this.settingsForm.get('features.users') as FormGroup;
    const alertGroup = this.settingsForm.get('features.alertTimer') as FormGroup;

    usersGroup.get('enabled')?.valueChanges.subscribe(enabled => {
      if (enabled) usersGroup.get('limit')?.enable({ emitEvent: false });
      else usersGroup.get('limit')?.disable({ emitEvent: false });
    });
    if (!usersGroup.get('enabled')?.value) usersGroup.get('limit')?.disable({ emitEvent: false });

    alertGroup.get('enabled')?.valueChanges.subscribe(enabled => {
      if (enabled) alertGroup.get('minutes')?.enable({ emitEvent: false });
      else alertGroup.get('minutes')?.disable({ emitEvent: false });
    });
    if (!alertGroup.get('enabled')?.value) alertGroup.get('minutes')?.disable({ emitEvent: false });

    this.setupAutoMessageControls(this.settingsForm.get('features.automaticMessages') as FormGroup);
  }

  private setupAutoMessageControls(group: FormGroup) {
    const msgKeys = ['newConversation', 'assigned', 'active', 'closed'];

    const applyAll = (masterEnabled: boolean) => {
      msgKeys.forEach(key => {
        const sub = group.get(key) as FormGroup;
        if (masterEnabled) {
          sub.get('enabled')?.enable({ emitEvent: false });
          if (sub.get('enabled')?.value) sub.get('text')?.enable({ emitEvent: false });
          else sub.get('text')?.disable({ emitEvent: false });
        } else {
          sub.get('enabled')?.disable({ emitEvent: false });
          sub.get('text')?.disable({ emitEvent: false });
        }
      });
    };

    group.get('enabled')?.valueChanges.subscribe(enabled => applyAll(enabled));
    msgKeys.forEach(key => {
      const sub = group.get(key) as FormGroup;
      sub.get('enabled')?.valueChanges.subscribe(subEnabled => {
        if (group.get('enabled')?.value) {
          if (subEnabled) sub.get('text')?.enable({ emitEvent: false });
          else sub.get('text')?.disable({ emitEvent: false });
        }
      });
    });

    applyAll(group.get('enabled')?.value ?? false);
  }


  save() {
    let settingsToSave = this.settingsForm.value;

    this.ref.close({
      action: DialogAction.OK,
      data: settingsToSave
    });
  }

  closeDialog(){
    this.ref.close({
      action: DialogAction.CLOSE,
    });
  }

  ngOnDestroy() {
    if (this.ref) {
      this.ref.close();
    }
  }

}
